import { EMPTY_MODULE_API_INFO } from "@client/content/lib/consts";
import { type ModuleApiInfo } from "@client/content/selectors";
import { globals } from "@client/globals";
import { type ApiResponse, type HttpApiResponse } from "@client/types/Api";
import { type AppState } from "@client/types/AppState";
import { type GenericMap } from "@client/types/GenericMap";
import { parseQueryString, replaceQueryString } from "@common/lib/url";
import { generateId } from "./random";
import { request } from "./request";

export {
  addQueryString,
  parseQueryString,
  replaceQueryString,
  updateQueryString,
  upsertQueryString
} from "@common/lib/url";

export const API_INFO: ModuleApiInfo = EMPTY_MODULE_API_INFO;

export const getApiMediaPreviewUrl = (state: AppState) =>
  `${globals.paths.apiUrl}/DesktopModules/WebSitesCreative/Next/Api/Media/Preview?portalId=${state.portal.id}&pageId=${state.page.variantId}`;
export const getApiImagePreviewUrl = (state: AppState) => `${getApiMediaPreviewUrl(state)}&type=image`;

export const parseString = (s: string, separator: string, fn?: (value: string) => string): GenericMap<string> => {
  return s.split(separator).reduce((res, keyValue) => {
    const parts = keyValue.split("=");
    return {
      ...res,
      [parts[0]]: fn ? fn(parts[1]) : parts[1]
    };
  }, {});
};

export const updateUrlState = (update: (data: GenericMap<any>) => void) => {
  const qs = parseQueryString() || {};
  update(qs);
  pushUrlState(qs);
};

export const pushUrlState = (data: GenericMap<any>) => {
  const params = Object.keys(data)
    .filter((key) => data[key])
    .map((key) => `${key}=${data[key]}`);
  const newUrl =
    window.location.protocol + "//" + window.location.host + window.location.pathname + params.length
      ? `?${params.join("&")}`
      : "" + window.location.hash;
  window.history.pushState({ path: newUrl }, "", newUrl);
};

export interface IntegrationInfo {
  baseUrl: string;
  apiKey: string;
}
export const getIntegration = async (apiUrl: string, id: string): Promise<IntegrationInfo | undefined> => {
  const r = await request<ApiResponse<IntegrationInfo>>({
    cache: "memory",
    method: "GET",
    url: `${apiUrl}/misc/integration/${id}`
  });

  if (r.status !== 200 || !r.response || r.response.status !== "success") return;

  return r.response.data;
};

type ResolvePageUrlArgs = {
  cultureCode: string | null;
  postId?: string;
  productId?: string;
};
export const resolvePageUrl = async (baseUrl: string, pageId: string, args: ResolvePageUrlArgs): Promise<string> => {
  const r = await request<ApiResponse<string>>({
    cache: "memory",
    method: "GET",
    url: `${baseUrl}/misc/page/${pageId}/url`,
    query: args
  });

  if (r.status !== 200 || !r.response || r.response.status !== "success") return "";

  return r.response.data;
};

export const getAvoidCacheUrl = (url: string | null | undefined): string | null =>
  url ? replaceQueryString(url, { v: generateId() }) : null;

export const getUnwrapResponse =
  <T>(fn: (...args: any) => Promise<HttpApiResponse<T>>) =>
  (...args: any) =>
    fn(...args).then((x) => x.response!.data);

export const unwrapResponsePromise = async <T>(promise: Promise<HttpApiResponse<T>>): Promise<T> => {
  const res = await promise;
  return unwrapResponse(res);
};

export const unwrapResponse = <T>(res: HttpApiResponse<T>): T => {
  if (res.error) throw new Error(res.error);
  return res.response!.data;
};

export const unwrapResponseSafe = <T>(res: HttpApiResponse<T>, defaultValue: T): T => {
  if (res.error) return defaultValue;
  return res.response!.data;
};
